import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Mismos colores que el menú lateral del admin (AdminHomeLayout.jsx).
const ACCENT = 'var(--wb-accent)';
const TEXT = 'var(--wb-text)';
const MUTED = 'var(--wb-muted-70)';
const INDENT_PX = 20;

const rowSx = (level) => ({
  borderRadius: 1,
  py: 0.5,
  pl: 0.5 + (level * INDENT_PX) / 8,
  color: TEXT,
  '&:hover': { backgroundColor: 'var(--wb-x-bg-0ea5e91f, rgba(14, 165, 233, 0.12))' },
  '&.Mui-selected': { backgroundColor: 'var(--wb-accent-tint)', color: ACCENT },
  '&.Mui-selected:hover': { backgroundColor: 'var(--wb-x-bg-0ea5e933, rgba(14, 165, 233, 0.2))' }
});

const Hint = ({ level, children }) => (
  <Typography variant="body2" sx={{ color: MUTED, pl: 0.5 + ((level * INDENT_PX) + 28) / 8, py: 0.75, fontSize: '0.85rem' }}>
    {children}
  </Typography>
);

const Loading = ({ level }) => (
  <Box sx={{ pl: 0.5 + ((level * INDENT_PX) + 28) / 8, py: 0.75 }}>
    <CircularProgress size={16} sx={{ color: ACCENT }} />
  </Box>
);

/**
 * Fila del árbol: chevron (solo expande/colapsa), ícono, nombre y un
 * contador opcional. Clic en la fila = seleccionar (y expandir si no lo
 * estaba). `expandable=false` para las hojas (tablas).
 */
const TreeRow = ({ level, label, icon, selected, expanded, expandable, onSelect, onToggle, trailing, title }) => (
  <ListItemButton
    selected={selected}
    onClick={onSelect}
    sx={rowSx(level)}
    title={title || label}
    aria-expanded={expandable ? expanded : undefined}
  >
    <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
      {expandable && (
        <IconButton
          size="small"
          aria-label={expanded ? `Contraer ${label}` : `Expandir ${label}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
          sx={{ color: MUTED, p: 0.25 }}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      )}
    </Box>
    <ListItemIcon sx={{ minWidth: 30, color: selected ? ACCENT : MUTED }}>{icon}</ListItemIcon>
    <ListItemText
      primary={label}
      primaryTypographyProps={{ noWrap: true, sx: { fontSize: '0.9rem', fontWeight: level === 0 ? 600 : 400 } }}
    />
    {trailing}
  </ListItemButton>
);

const Count = ({ value }) => (
  <Typography component="span" sx={{ color: MUTED, fontSize: '0.75rem', ml: 1, flexShrink: 0 }}>
    {value}
  </Typography>
);

/**
 * Explorador del catálogo Iceberg en forma de árbol: Database → Schema →
 * Tabla. Solo presentación: los datos, la carga perezosa y la selección
 * viven en IcebergCatalog.jsx.
 */
const CatalogTree = ({
  databases,
  schemasByDatabase,
  tablesBySchema,
  expandedDatabases,
  expandedSchemas,
  loadingSchemasFor,
  loadingTablesFor,
  selected,
  onToggleDatabase,
  onToggleSchema,
  onSelect
}) => (
  <List dense disablePadding aria-label="Explorador del catálogo Iceberg">
    {databases.map((database) => {
      const databaseId = database.iceberg_database_id;
      const databaseExpanded = Boolean(expandedDatabases[databaseId]);
      const schemas = schemasByDatabase[databaseId];
      return (
        <React.Fragment key={databaseId}>
          <TreeRow
            level={0}
            label={database.name}
            icon={<StorageIcon fontSize="small" />}
            expandable
            expanded={databaseExpanded}
            selected={selected?.type === 'database' && selected.databaseId === databaseId}
            onToggle={() => onToggleDatabase(database)}
            onSelect={() => onSelect({ type: 'database', databaseId }, () => !databaseExpanded && onToggleDatabase(database))}
            trailing={<Count value={database.schema_count ?? 0} />}
          />
          {databaseExpanded && (
            loadingSchemasFor === databaseId ? <Loading level={1} />
              : !schemas?.length ? <Hint level={1}>Sin schemas</Hint>
                : schemas.map((schema) => {
                  const schemaId = schema.iceberg_schema_id;
                  const schemaExpanded = Boolean(expandedSchemas[schemaId]);
                  const tables = tablesBySchema[schemaId];
                  return (
                    <React.Fragment key={schemaId}>
                      <TreeRow
                        level={1}
                        label={schema.name}
                        icon={<FolderOutlinedIcon fontSize="small" />}
                        expandable
                        expanded={schemaExpanded}
                        selected={selected?.type === 'schema' && selected.schemaId === schemaId}
                        onToggle={() => onToggleSchema(schema)}
                        onSelect={() => onSelect(
                          { type: 'schema', databaseId, schemaId },
                          () => !schemaExpanded && onToggleSchema(schema)
                        )}
                        trailing={tables ? <Count value={tables.length} /> : null}
                      />
                      {schemaExpanded && (
                        loadingTablesFor === schemaId ? <Loading level={2} />
                          : !tables?.length ? <Hint level={2}>Sin tablas</Hint>
                            : tables.map((table) => (
                              <TreeRow
                                key={table.glue_table_name}
                                level={2}
                                label={table.glue_table_name}
                                icon={<TableChartOutlinedIcon fontSize="small" />}
                                expandable={false}
                                selected={selected?.type === 'table' && selected.schemaId === schemaId
                                  && selected.tableName === table.glue_table_name}
                                onSelect={() => onSelect({ type: 'table', databaseId, schemaId, tableName: table.glue_table_name })}
                                trailing={table.has_pending_schema_review ? (
                                  <Tooltip title="Cambio de esquema pendiente de revisión">
                                    <WarningAmberIcon fontSize="small" sx={{ color: 'var(--wb-warning)', ml: 1 }} />
                                  </Tooltip>
                                ) : null}
                              />
                            ))
                      )}
                    </React.Fragment>
                  );
                })
          )}
        </React.Fragment>
      );
    })}
  </List>
);

export default CatalogTree;
